import React from "react";

import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import { rest, t } from "@auto/services";
import { getServiceUri } from "@auto/metadata";
import {
  loadDepartments,
  loadArrondissements,
  loadCommunes,
  loadConstituencies,
  loadSections
} from "@src/reporting/services/reportingApi.mjs";

import styles from "./VotingCenterReportsFacets.module.styl";

const GEOGRAPHY_FIELDS = Object.freeze([
  { id: "departmentGid", name: "departmentName", label: "person.department" },
  { id: "arrondissementGid", name: "arrondissementName", label: "geo.arrondissement" },
  { id: "communeGid", name: "communeName", label: "person.commune" },
  { id: "sectionGid", name: "sectionName", label: "geo.communalSection" },
  { id: "constituencyGid", name: "constituencyName", label: "geo.constituency" }
]);

export const DEFAULT_VOTING_CENTER_REPORT_FILTERS = Object.freeze({
  disabledCenters: "exclude",
  departmentGid: "",
  arrondissementGid: "",
  communeGid: "",
  sectionGid: "",
  constituencyGid: ""
});

const DISABLED_CENTER_STATES = Object.freeze([
  { value: "exclude", label: "filter.votingCenterHide" },
  { value: "include", label: "filter.votingCenterInclude" },
  { value: "only", label: "filter.votingCenterOnly" }
]);

const normalizeChoice = (value, allowed, fallback) => {
  return allowed.includes(value) ? value : fallback;
};

const normalizeGid = (value, field) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  const raw = String(value).trim();
  if (!/^[1-9]\d*$/.test(raw) || !Number.isSafeInteger(Number(raw))) {
    throw new Error(`Invalid geography filter: ${field}`);
  }
  return raw;
};

export const normalizeVotingCenterReportFilters = (value) => {
  const input = value && typeof value === "object" && !Array.isArray(value)
    ? value
    : {};

  const result = {
    disabledCenters: normalizeChoice(
      input.disabledCenters,
      ["exclude", "include", "only"],
      DEFAULT_VOTING_CENTER_REPORT_FILTERS.disabledCenters
    )
  };
  for (const field of GEOGRAPHY_FIELDS) {
    result[field.id] = normalizeGid(input[field.id], field.id);
    result[field.name] = String(input[field.name] || "");
  }
  return result;
};

export const isDefaultVotingCenterReportFilters = (value) => {
  const filters = normalizeVotingCenterReportFilters(value);
  return filters.disabledCenters === DEFAULT_VOTING_CENTER_REPORT_FILTERS.disabledCenters
    && GEOGRAPHY_FIELDS.every((field) => !filters[field.id]);
};

export const getVotingCenterReportActiveFilterCount = (value) => {
  const filters = normalizeVotingCenterReportFilters(value);
  return Number(filters.disabledCenters !== DEFAULT_VOTING_CENTER_REPORT_FILTERS.disabledCenters)
    + GEOGRAPHY_FIELDS.filter((field) => Boolean(filters[field.id])).length;
};

export const buildVotingCenterReportBackendFilters = (value) => {
  const filters = normalizeVotingCenterReportFilters(value);
  const result = {
    status: filters.disabledCenters === "include"
      ? ["ENABLED", "DISABLED"]
      : filters.disabledCenters === "only"
        ? ["DISABLED"]
        : ["ENABLED"]
  };

  for (const field of GEOGRAPHY_FIELDS) {
    if (filters[field.id]) {
      result[field.id] = Number(filters[field.id]);
    }
  }
  return result;
};

export const loadVotingCenterReportFacetCounts = async ({ search, filters }) => {
  return await rest.request(
    getServiceUri() + "reporting/voting-center-reports/facets",
    "POST",
    {
      search: String(search || "").trim(),
      filters: buildVotingCenterReportBackendFilters(filters)
    }
  );
};

const countFor = (counts, path, fallback = 0) => {
  let current = counts || {};

  for (const key of path) {
    if (!current || typeof current !== "object") {
      return fallback;
    }

    current = current[key];
  }

  const number = Number(current);
  return Number.isFinite(number) ? number : fallback;
};

const Chip = ({ label, onRemove }) => (
  <button
    type="button"
    className={styles.chip}
    onClick={onRemove}
  >
    <span>{label}</span>
    <span className={styles.chipClose}>×</span>
  </button>
);

const DisabledCenterFilter = ({ filters, counts, onSet }) => {
  const selected = filters.disabledCenters;
  const disabledCount = countFor(counts, ["status", "DISABLED"]);

  return (
    <div className={styles.exclusionCompactRow}>
      <div className={styles.exclusionCompactMeta}>
        <span className={styles.exclusionCompactName}>
          {t("filter.disabledCenters")}
        </span>
        <span className={styles.exclusionCompactCount}>
          {disabledCount}
        </span>
      </div>

      <div className={styles.segmentedControl}>
        {DISABLED_CENTER_STATES.map((state) => (
          <button
            key={state.value}
            type="button"
            className={
              selected === state.value
                ? `${styles.segmentedButton} ${styles.segmentedButtonSelected}`
                : styles.segmentedButton
            }
            onClick={() => onSet(state.value)}
          >
            {t(state.label)}
          </button>
        ))}
      </div>
    </div>
  );
};

export const VotingCenterReportsFacetButton = ({ controller }) => {
  const activeCount = controller?.activeCount || 0;

  return (
    <button
      type="button"
      className={
        activeCount
          ? `${styles.filterButton} ${styles.filterButtonActive}`
          : styles.filterButton
      }
      onClick={controller.toggleDrawer}
    >
      <span className={styles.filterButtonIcon}>
        <TuneIcon fontSize="inherit" />
      </span>
      <span className={styles.filterButtonLabel}>
        {t("common.filters")}
      </span>
      {activeCount ? (
        <span className={styles.filterButtonBadge}>
          {activeCount}
        </span>
      ) : null}
    </button>
  );
};

export const VotingCenterReportsFacetChips = ({ controller }) => {
  const filters = normalizeVotingCenterReportFilters(controller?.value);

  if (isDefaultVotingCenterReportFilters(filters)) {
    return null;
  }

  const state = DISABLED_CENTER_STATES.find(
    (item) => item.value === filters.disabledCenters
  );
  const activeGeography = GEOGRAPHY_FIELDS.filter((field) => filters[field.id]);

  return (
    <div className={styles.chipsBar}>
      {filters.disabledCenters !== "exclude" ? (
        <Chip
          label={`${t("filter.disabledCenters")}: ${t(state.label)}`}
          onRemove={() => controller.patchValue({ disabledCenters: "exclude" })}
        />
      ) : null}

      {activeGeography.map((field) => (
        <Chip
          key={field.id}
          label={`${t(field.label)}: ${filters[field.name] || filters[field.id]}`}
          onRemove={() => {
            const patch = { [field.id]: "", [field.name]: "" };
            if (field.id === "communeGid") {
              patch.sectionGid = "";
              patch.sectionName = "";
            }
            controller.patchValue(patch);
          }}
        />
      ))}

      <button
        type="button"
        className={styles.clearAll}
        onClick={controller.reset}
      >
        {t("common.clearAll")}
      </button>
    </div>
  );
};

let geographyOptionsPromise = null;

const loadGeographyOptions = () => {
  if (!geographyOptionsPromise) {
    geographyOptionsPromise = Promise.all([
      loadDepartments(),
      loadArrondissements(),
      loadCommunes(),
      loadConstituencies()
    ]).then(([departments, arrondissements, communes, constituencies]) => ({
      departments,
      arrondissements,
      communes,
      constituencies
    })).catch((error) => {
      geographyOptionsPromise = null;
      throw error;
    });
  }
  return geographyOptionsPromise;
};

const sameGid = (a, b) => String(a ?? "") === String(b ?? "");

const GeographySelect = ({ label, emptyLabel, value, rows, disabled, onChange }) => (
  <label className={styles.geographyField}>
    <span className={styles.geographyFieldLabel}>{t(label)}</span>
    <select
      className={styles.geographySelect}
      value={value || ""}
      disabled={disabled}
      onChange={(event) => {
        const gid = event.target.value;
        const selected = (rows || []).find((row) => sameGid(row.gid, gid));
        onChange(gid, selected?.name || "");
      }}
    >
      <option value="">{t(emptyLabel)}</option>
      {(rows || []).map((row) => (
        <option key={row.gid} value={String(row.gid)}>{row.name}</option>
      ))}
    </select>
  </label>
);

const GeographyFilters = ({ filters, onPatch }) => {
  const [roots, setRoots] = React.useState(null);
  const [sections, setSections] = React.useState([]);
  const [error, setError] = React.useState("");
  const [loadingSections, setLoadingSections] = React.useState(false);

  React.useEffect(() => {
    let active = true;
    loadGeographyOptions()
      .then((result) => {
        if (active) {
          setRoots(result);
          setError("");
        }
      })
      .catch((reason) => {
        if (active) setError(reason?.message || String(reason));
      });
    return () => { active = false; };
  }, []);

  React.useEffect(() => {
    if (!filters.communeGid) {
      setSections([]);
      setLoadingSections(false);
      return undefined;
    }

    let active = true;
    setLoadingSections(true);
    loadSections(filters.communeGid)
      .then((rows) => {
        if (active) {
          setSections(rows);
          setError("");
        }
      })
      .catch((reason) => {
        if (active) setError(reason?.message || String(reason));
      })
      .finally(() => {
        if (active) setLoadingSections(false);
      });
    return () => { active = false; };
  }, [filters.communeGid]);

  const departments = roots?.departments || [];
  const arrondissements = roots?.arrondissements || [];
  const communes = roots?.communes || [];
  const constituencies = roots?.constituencies || [];
  const arrondissementByGid = new Map(arrondissements.map((row) => [String(row.gid), row]));

  const arrondissementRows = arrondissements.filter((row) =>
    !filters.departmentGid || sameGid(row.departmentGid, filters.departmentGid)
  );
  const communeRows = communes.filter((row) => {
    if (filters.arrondissementGid && !sameGid(row.arrondissementGid, filters.arrondissementGid)) return false;
    if (filters.constituencyGid && !sameGid(row.constituencyGid, filters.constituencyGid)) return false;
    if (filters.departmentGid) {
      const arrondissement = arrondissementByGid.get(String(row.arrondissementGid));
      if (!arrondissement || !sameGid(arrondissement.departmentGid, filters.departmentGid)) return false;
    }
    return true;
  });
  const constituencyRows = constituencies.filter((row) => {
    if (!filters.departmentGid && !filters.arrondissementGid && !filters.communeGid) return true;
    return communes.some((commune) => {
      if (!sameGid(commune.constituencyGid, row.gid)) return false;
      if (filters.communeGid && !sameGid(commune.gid, filters.communeGid)) return false;
      if (filters.arrondissementGid && !sameGid(commune.arrondissementGid, filters.arrondissementGid)) return false;
      const arrondissement = arrondissementByGid.get(String(commune.arrondissementGid));
      return !filters.departmentGid || (arrondissement && sameGid(arrondissement.departmentGid, filters.departmentGid));
    });
  });

  const set = (field, gid, name, clearFields = []) => {
    const patch = { [field]: gid, [field.replace("Gid", "Name")]: name };
    clearFields.forEach((next) => {
      patch[next] = "";
      patch[next.replace("Gid", "Name")] = "";
    });
    onPatch(patch);
  };

  return (
    <div className={styles.geographyFilters}>
      {error ? <p className={styles.geographyError} role="alert">{error}</p> : null}
      {!roots && !error ? <div className={styles.geographyLoading}>{t("common.loading")}</div> : null}
      <GeographySelect
        label="person.department"
        emptyLabel="reporting.allDepartments"
        value={filters.departmentGid}
        rows={departments}
        disabled={!roots}
        onChange={(gid, name) => set("departmentGid", gid, name,
          ["arrondissementGid", "communeGid", "sectionGid", "constituencyGid"])}
      />
      <GeographySelect
        label="geo.arrondissement"
        emptyLabel="reporting.allArrondissements"
        value={filters.arrondissementGid}
        rows={arrondissementRows}
        disabled={!roots}
        onChange={(gid, name) => set("arrondissementGid", gid, name,
          ["communeGid", "sectionGid", "constituencyGid"])}
      />
      <GeographySelect
        label="person.commune"
        emptyLabel="reporting.allCommunes"
        value={filters.communeGid}
        rows={communeRows}
        disabled={!roots}
        onChange={(gid, name) => set("communeGid", gid, name, ["sectionGid"])}
      />
      <GeographySelect
        label="geo.communalSection"
        emptyLabel="reporting.allSections"
        value={filters.sectionGid}
        rows={sections}
        disabled={!roots || !filters.communeGid || loadingSections}
        onChange={(gid, name) => set("sectionGid", gid, name)}
      />
      <GeographySelect
        label="geo.constituency"
        emptyLabel="reporting.allConstituencies"
        value={filters.constituencyGid}
        rows={constituencyRows}
        disabled={!roots}
        onChange={(gid, name) => set("constituencyGid", gid, name)}
      />
    </div>
  );
};

const VotingCenterReportsFacets = ({ controller }) => {
  const filters = normalizeVotingCenterReportFilters(controller?.value);
  const counts = controller?.counts || {};

  return (
    <div className={styles.drawer}>
      <div className={styles.drawerHeader}>
        <div>
          <div className={styles.drawerTitle}>
            {t("common.filters")}
          </div>
          <div className={styles.drawerSubtitle}>
            {controller.countsLoading
              ? t("common.updatingCounts")
              : t("filter.refineVotingCenters")}
          </div>
        </div>

        <button
          type="button"
          className={styles.closeButton}
          onClick={controller.closeDrawer}
          aria-label={t("common.closeFilters")}
        >
          <CloseIcon fontSize="inherit" />
        </button>
      </div>

      <div className={styles.drawerBody}>
        <section className={styles.section}>
          <div className={styles.sectionTitle}>{t("reporting.geographicScope")}</div>
          <GeographyFilters
            filters={filters}
            onPatch={controller.patchValue}
          />
        </section>
        <section className={styles.section}>
          <div className={styles.sectionTitle}>
            {t("filter.availability")}
          </div>

          <div className={styles.exclusionHelp}>
            {t("filter.disabledCentersHelp")}
          </div>

          <DisabledCenterFilter
            filters={filters}
            counts={counts}
            onSet={(state) => controller.patchValue({ disabledCenters: state })}
          />
        </section>
      </div>

      <div className={styles.drawerFooter}>
        <button
          type="button"
          className={styles.secondaryAction}
          onClick={controller.reset}
          disabled={controller.activeCount === 0}
        >
          <RestartAltIcon fontSize="inherit" />
          <span>{t("common.resetFilters")}</span>
        </button>

        <button
          type="button"
          className={styles.primaryAction}
          onClick={controller.closeDrawer}
        >
          {t("common.done")}
        </button>
      </div>
    </div>
  );
};

export const VOTING_CENTER_REPORTS_FACETS = {
  storageKey: "haiti.voting-center-reports.facets.v1",
  overlayLabel: t("filter.votingCenterReportFilters"),
  defaultValue: DEFAULT_VOTING_CENTER_REPORT_FILTERS,
  normalizeValue: normalizeVotingCenterReportFilters,
  isDefaultValue: isDefaultVotingCenterReportFilters,
  getActiveCount: getVotingCenterReportActiveFilterCount,
  loadCounts: loadVotingCenterReportFacetCounts,
  renderButton: (controller) => (
    <VotingCenterReportsFacetButton controller={controller} />
  ),
  renderDrawer: (controller) => (
    <VotingCenterReportsFacets controller={controller} />
  ),
  renderChips: (controller) => (
    <VotingCenterReportsFacetChips controller={controller} />
  )
};

export default VotingCenterReportsFacets;
